import React, { useState, useEffect, useRef } from 'react';
import { useContent } from '../../context/ContentContext';
import { resolveMediaUrl } from '../../utils/api';

export interface PageSkeletonLoaderProps {
  onComplete?: () => void;
}

const LOADING_LOGO_ASSET = '/assets/aistudio/image_e0bd354b.png';
const LOADING_PRODUCT_ASSET = '/assets/aistudio/image_69ec5ced.png';

const SCENE_1_DURATION_MS = 1400;
const SCENE_2_DURATION_MS = 2150;
const TOTAL_DURATION_MS = 5000;
const SAFETY_TIMEOUT_MS = 6800;

interface SplashWindow extends Window {
  __initialLoadingAnimReady?: boolean;
  __initialLoadingAnimEnded?: boolean;
  __initialLoadingAnimFailed?: boolean;
  __dismissInitialLoadingOverlay?: (immediate?: boolean) => void;
}

export const PageSkeletonLoader: React.FC<PageSkeletonLoaderProps> = ({ onComplete }) => {
  const { data, isDataReady } = useContent();
  const { siteSettings } = data || {};
  const brandName = siteSettings?.brandName || 'LETONKU';
  const customLogoUrl = siteSettings?.logoUrl ? resolveMediaUrl(siteSettings.logoUrl) : '/logo_icon.jpg';

  const [hasHtmlOverlay] = useState<boolean>(() => {
    if (typeof document === 'undefined') return false;
    return Boolean(document.getElementById('initial-loading-overlay'));
  });

  const completedRef = useRef<boolean>(false);
  const [activeScene, setActiveScene] = useState<1 | 2 | 3>(1);

  const [animReady, setAnimReady] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const win = window as unknown as SplashWindow;
    return Boolean(win.__initialLoadingAnimReady);
  });

  const [animError, setAnimError] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const win = window as unknown as SplashWindow;
    return Boolean(win.__initialLoadingAnimFailed);
  });

  const [animEnded, setAnimEnded] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const win = window as unknown as SplashWindow;
    return Boolean(win.__initialLoadingAnimEnded);
  });

  const [fallbackImageError, setFallbackImageError] = useState(false);

  // 1. App readiness completion logic:
  // Completes strictly when the 3-scene animation finishes (or fallback is active due to asset error) AND app data is ready.
  useEffect(() => {
    if ((animEnded || animError) && isDataReady && !completedRef.current) {
      completedRef.current = true;
      if (onComplete) onComplete();
    }
  }, [animEnded, animError, isDataReady, onComplete]);

  // 2. Coordinate with the initial HTML splash 3-scene controller (or run standalone controller if mounted directly)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const win = window as unknown as SplashWindow;
    const initialStage = document.getElementById('initial-loading-stage');

    if (hasHtmlOverlay && initialStage) {
      const syncFromHtmlStage = () => {
        if (win.__initialLoadingAnimReady || initialStage.classList.contains('is-ready')) {
          setAnimReady(true);
        }
        if (win.__initialLoadingAnimEnded) {
          setAnimEnded(true);
        }
        if (win.__initialLoadingAnimFailed) {
          setAnimError(true);
        }
      };

      syncFromHtmlStage();

      window.addEventListener('leton-splash-anim-state', syncFromHtmlStage);

      const fallbackTimer = setTimeout(() => {
        if (!completedRef.current) {
          setAnimError(true);
        }
      }, SAFETY_TIMEOUT_MS);

      return () => {
        clearTimeout(fallbackTimer);
        window.removeEventListener('leton-splash-anim-state', syncFromHtmlStage);
      };
    }

    // Fallback standalone controller path ONLY if #initial-loading-overlay was not present in DOM
    if (win.__initialLoadingAnimEnded) {
      setAnimEnded(true);
      return;
    }
    if (win.__initialLoadingAnimFailed) {
      setAnimError(true);
      return;
    }

    let scene2Timer: ReturnType<typeof setTimeout> | null = null;
    let scene3Timer: ReturnType<typeof setTimeout> | null = null;
    let completeTimer: ReturnType<typeof setTimeout> | null = null;
    let safetyTimer: ReturnType<typeof setTimeout> | null = null;
    let started = false;

    const clearTimers = () => {
      if (scene2Timer) clearTimeout(scene2Timer);
      if (scene3Timer) clearTimeout(scene3Timer);
      if (completeTimer) clearTimeout(completeTimer);
      if (safetyTimer) clearTimeout(safetyTimer);
    };

    const startSequence = () => {
      if (started) return;
      started = true;
      setActiveScene(1);
      setAnimReady(true);
      setAnimError(false);

      scene2Timer = setTimeout(() => {
        setActiveScene(2);
      }, SCENE_1_DURATION_MS);

      scene3Timer = setTimeout(() => {
        setActiveScene(3);
      }, SCENE_1_DURATION_MS + SCENE_2_DURATION_MS);

      completeTimer = setTimeout(() => {
        setAnimEnded(true);
      }, TOTAL_DURATION_MS);
    };

    safetyTimer = setTimeout(() => {
      if (!completedRef.current) {
        setAnimError(true);
      }
    }, SAFETY_TIMEOUT_MS);

    const preloadLogo = new Image();
    const preloadProduct = new Image();

    preloadLogo.onerror = () => {
      clearTimers();
      setAnimError(true);
    };
    preloadProduct.onerror = () => {
      clearTimers();
      setAnimError(true);
    };

    preloadLogo.onload = () => {
      startSequence();
    };

    preloadLogo.src = LOADING_LOGO_ASSET;
    preloadProduct.src = LOADING_PRODUCT_ASSET;

    if (preloadLogo.complete && preloadLogo.naturalWidth > 0) {
      startSequence();
    }

    return () => {
      clearTimers();
    };
  }, [hasHtmlOverlay]);

  // When #initial-loading-overlay (z-index: 9999) is active in index.html, render a clean matching white backdrop
  // underneath so the transition to the app content is completely seamless.
  if (hasHtmlOverlay) {
    return (
      <div
        className="fixed inset-0 w-screen h-screen min-h-[100dvh] z-50 bg-white select-none overflow-hidden m-0 p-0 pointer-events-auto"
        aria-hidden="true"
      />
    );
  }

  return (
    <div className="fixed inset-0 w-[100vw] h-[100vh] min-h-[100dvh] h-[100dvh] z-50 bg-white select-none overflow-hidden m-0 p-0 pointer-events-auto">
      {/* Fallback screen if assets fail to load or before stage is ready */}
      {(!animReady || animError) && (
        <div className="fixed inset-0 w-[100vw] h-[100vh] min-h-[100dvh] h-[100dvh] z-[10] flex flex-col items-center justify-center text-center px-6 pointer-events-none transition-opacity duration-200">
          <div className="w-24 h-24 rounded-full p-1 bg-white/95 shadow-lg flex items-center justify-center overflow-hidden">
            {!fallbackImageError ? (
              <img
                src={customLogoUrl}
                alt={brandName}
                onError={() => setFallbackImageError(true)}
                className="w-full h-full object-contain object-center rounded-full"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-full h-full rounded-full bg-[#0284C7] flex flex-col items-center justify-center text-white p-1">
                <span className="font-display font-black text-sm tracking-wider uppercase leading-none">
                  LETONKU
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {!animError && (
        <div
          className={`leton-loading-stage ${animReady ? 'is-ready' : ''}`}
          data-active-scene={activeScene}
        >
          {/* SCENE 1: OPENING LOGO */}
          <div className={`leton-scene leton-scene-1 ${activeScene === 1 ? 'is-active' : ''}`}>
            <div className="leton-scene-media-box">
              <img
                src={LOADING_LOGO_ASSET}
                alt="LetonKu Opening Logo"
                decoding="async"
                onError={() => setAnimError(true)}
                className="leton-scene-img leton-logo-img-bounds leton-logo-opening-img"
              />
              <div className="leton-blue-glow" />
              <div className="leton-shimmer-mask">
                <div className="leton-light-shimmer" />
              </div>
            </div>
          </div>

          {/* SCENE 2: PRODUCT SHOWCASE */}
          <div className={`leton-scene leton-scene-2 ${activeScene === 2 ? 'is-active' : ''}`}>
            <div className="leton-scene-media-box">
              <img
                src={LOADING_PRODUCT_ASSET}
                alt="Leton Coffee Iced Coffee Showcase"
                decoding="async"
                onError={() => setAnimError(true)}
                className="leton-scene-img leton-product-img-bounds leton-product-showcase-img"
              />
              <div className="leton-product-ambient" />
              <div className="leton-parallax-accents" aria-hidden="true">
                <div className="leton-ice-cube leton-ice-cube--1" />
                <div className="leton-ice-cube leton-ice-cube--2" />
                <div className="leton-ice-cube leton-ice-cube--3" />
                <div className="leton-splash-drop leton-splash-drop--1" />
                <div className="leton-splash-drop leton-splash-drop--2" />
                <div className="leton-splash-drop leton-splash-drop--3" />
                <div className="leton-splash-drop leton-splash-drop--4" />
              </div>
            </div>
          </div>

          {/* SCENE 3: CLOSING LOGO */}
          <div className={`leton-scene leton-scene-3 ${activeScene === 3 ? 'is-active' : ''}`}>
            <div className="leton-scene-media-box">
              <img
                src={LOADING_LOGO_ASSET}
                alt="LetonKu Closing Logo"
                decoding="async"
                onError={() => setAnimError(true)}
                className="leton-scene-img leton-logo-img-bounds leton-logo-closing-img"
              />
              <div className="leton-blue-glow" />
              <div className="leton-shimmer-mask">
                <div className="leton-light-shimmer" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


